import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Modal, Form, Spinner, Alert, InputGroup } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faEnvelope, faPhone, faIdCard, faExclamationTriangle, faCheckCircle, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { postApi } from "../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { LOGIN } from "../redux/actions/actionTypes";
import { toast } from "react-hot-toast";

function ProfileCompletionModal() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const checkDone = useRef(false);
  const otpIntervalRef = useRef(null);

  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", phone: "", designation: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);

  const isPhoneValid = /^0\d{10}$/.test(form.phone);

  const stopOtpTimer = useCallback(() => {
    if (otpIntervalRef.current) {
      clearInterval(otpIntervalRef.current);
      otpIntervalRef.current = null;
    }
  }, []);

  const startOtpTimer = useCallback(() => {
    stopOtpTimer();
    setOtpTimer(60);
    otpIntervalRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(otpIntervalRef.current);
          otpIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopOtpTimer]);

  useEffect(() => {
    return () => stopOtpTimer();
  }, [stopOtpTimer]);

  useEffect(() => {
    if (checkDone.current) return;
    if (Object.keys(user).length === 0) return;
    checkDone.current = true;

    const validPhone = /^0\d{10}$/.test(user?.phone);
    if (!user?.email || !validPhone) {
      setForm({
        email: user?.email || "",
        phone: user?.phone || "",
        designation: user?.designation || "",
      });
      setShow(true);
    }
  }, [user]);

  const validate = () => {
    const newErrors = {};
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^0\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter a valid Nigerian phone number (11 digits starting with 0)";
    }
    if (!phoneVerified) {
      newErrors.phone = "Please verify your phone number via OTP";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "phone" && (otpSent || phoneVerified)) {
      setOtpSent(false);
      setPhoneVerified(false);
      setOtpCode("");
      setOtpError("");
      stopOtpTimer();
      setOtpTimer(0);
    }
  };

  const handleSendOtp = async () => {
    if (!isPhoneValid) return;
    setSendingOtp(true);
    setOtpError("");
    try {
      const result = await postApi("send-otp", { phone: form.phone, email: form.email });
      if (result.success) {
        setOtpSent(true);
        startOtpTimer();
        toast.success("Verification code sent to your phone and email!");
      } else {
        toast.error(result.msg || "Could not send verification code. Please try again.");
      }
    } catch {
      toast.error("Could not send verification code. Please try again.");
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setVerifyingOtp(true);
    setOtpError("");
    try {
      const result = await postApi("verify-otp", { phone: form.phone, code: otpCode });
      if (result.success) {
        setPhoneVerified(true);
        setOtpError("");
        stopOtpTimer();
        setOtpTimer(0);
        toast.success("Phone number verified!");
      } else {
        setOtpError(result.msg || "Invalid verification code. Please try again.");
        setOtpCode("");
      }
    } catch {
      setOtpError("Verification failed. Please try again.");
      setOtpCode("");
    }
    setVerifyingOtp(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const resp = await postApi("treasury-app/update_contact_info", {
        email: form.email,
        phone: form.phone,
        designation: form.designation,
        username: user.username,
        fullname: user.fullname || "",
      });
      if (resp.success) {
        dispatch({
          type: LOGIN,
          payload: { ...user, email: form.email, phone: form.phone, designation: form.designation },
        });
        setShow(false);
        toast.success("Profile updated successfully!");
      } else {
        toast.error(resp.msg || "Failed to update profile");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleOtpInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(val);
    if (otpError) setOtpError("");
  };

  if (!show) return null;

  return (
    <Modal show={true} backdrop="static" keyboard={false} centered size="md">
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faUser} className="text-primary" />
          Complete Your Profile
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          <p className="text-muted mb-3">
            Please provide and verify your contact information before proceeding.
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
              Email Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              isInvalid={!!errors.email}
              placeholder="you@example.com"
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
              Phone Number <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                isInvalid={!!errors.phone || !!(errors.phone && !phoneVerified)}
                placeholder="08031234567"
                disabled={phoneVerified}
              />
              <Button
                variant={phoneVerified ? "success" : "outline-primary"}
                onClick={handleSendOtp}
                disabled={!isPhoneValid || sendingOtp || phoneVerified}
              >
                {sendingOtp ? (
                  <Spinner animation="border" size="sm" />
                ) : phoneVerified ? (
                  <FontAwesomeIcon icon={faCheckCircle} />
                ) : otpSent ? (
                  "Resend" + (otpTimer > 0 ? ` (${otpTimer}s)` : "")
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                    Send Code
                  </>
                )}
              </Button>
            </InputGroup>
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nigerian mobile number — 11 digits starting with 0
            </Form.Text>
          </Form.Group>

          {otpSent && !phoneVerified && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Verification Code</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={handleOtpInputChange}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  isInvalid={!!otpError}
                />
                <Button
                  variant="primary"
                  onClick={handleVerifyOtp}
                  disabled={otpCode.length !== 6 || verifyingOtp}
                >
                  {verifyingOtp ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </InputGroup>
              <Form.Control.Feedback type="invalid">{otpError}</Form.Control.Feedback>
              {otpError && (
                <Form.Text className="text-danger">{otpError}</Form.Text>
              )}
            </Form.Group>
          )}

          {phoneVerified && (
            <Alert variant="success" className="py-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Phone number verified successfully.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
              Job Title / Designation
            </Form.Label>
            <Form.Control
              type="text"
              name="designation"
              value={form.designation}
              onChange={handleChange}
              placeholder="e.g. Accountant, Director, etc."
            />
          </Form.Group>

          {Object.keys(errors).length > 0 && (
            <Alert variant="danger" className="py-2 mb-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
              Please fix the errors above.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" type="submit" disabled={saving || !phoneVerified}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save Profile"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ProfileCompletionModal;
